import type { DelegateCalendarEventRow } from './executiveCalendarDelegatedService';
import { parseISO } from 'date-fns';

export type EventsCacheBucket = {
  cacheKey: string;
  windowStartMs: number;
  windowEndMs: number;
  eventsById: Record<string, DelegateCalendarEventRow>;
  lastSyncAt: number;
};

export function eventsCacheKey(executiveUid: string, calendarIds: string[]): string {
  return `${executiveUid}|${[...calendarIds].sort().join('|')}`;
}

export function eventCacheId(ev: DelegateCalendarEventRow): string {
  return `${ev.calendarId}:${ev.id}`;
}

export function sizeOfEventCache(bucket: EventsCacheBucket | null): number {
  if (!bucket) return 0;
  return Object.keys(bucket.eventsById).length;
}

/** @deprecated Use sizeOfEventCache */
export const countCachedEvents = sizeOfEventCache;

export function eventOverlapsWindow(
  ev: DelegateCalendarEventRow,
  windowStartMs: number,
  windowEndMs: number,
): boolean {
  if (!ev.start) return false;
  try {
    const evStartMs = parseISO(ev.start).getTime();
    const evEndMs = ev.end ? parseISO(ev.end).getTime() : evStartMs;
    return evEndMs >= windowStartMs && evStartMs <= windowEndMs;
  } catch {
    return false;
  }
}

export function cacheCoversWindow(
  bucket: EventsCacheBucket | null,
  cacheKey: string,
  windowStartMs: number,
  windowEndMs: number,
): boolean {
  if (!bucket || bucket.cacheKey !== cacheKey) return false;
  return bucket.windowStartMs <= windowStartMs && bucket.windowEndMs >= windowEndMs;
}

export function mergeCacheFetch(
  bucket: EventsCacheBucket | null,
  cacheKey: string,
  fetched: DelegateCalendarEventRow[],
  fetchStartMs: number,
  fetchEndMs: number,
): EventsCacheBucket {
  const eventsById: Record<string, DelegateCalendarEventRow> = {};

  // Keep events outside this fetch window; replace everything inside it so
  // deletions from Google Calendar are not resurrected from cache.
  if (bucket?.cacheKey === cacheKey) {
    for (const key of Object.keys(bucket.eventsById)) {
      const ev = bucket.eventsById[key];
      if (!eventOverlapsWindow(ev, fetchStartMs, fetchEndMs)) {
        eventsById[key] = ev;
      }
    }
  }

  for (let i = 0; i < fetched.length; i++) {
    const ev = fetched[i];
    eventsById[eventCacheId(ev)] = ev;
  }

  const windowStartMs =
    bucket?.cacheKey === cacheKey
      ? Math.min(bucket.windowStartMs, fetchStartMs)
      : fetchStartMs;
  const windowEndMs =
    bucket?.cacheKey === cacheKey ? Math.max(bucket.windowEndMs, fetchEndMs) : fetchEndMs;

  return {
    cacheKey,
    windowStartMs,
    windowEndMs,
    eventsById,
    lastSyncAt: Date.now(),
  };
}

/** Immediately drop one or more events from the in-memory cache (e.g. after delete). */
export function removeEventsFromCache(
  bucket: EventsCacheBucket | null,
  cacheKey: string,
  eventKeys: string[],
): EventsCacheBucket | null {
  if (!bucket || bucket.cacheKey !== cacheKey || eventKeys.length === 0) return bucket;
  const eventsById = { ...bucket.eventsById };
  for (let i = 0; i < eventKeys.length; i++) {
    delete eventsById[eventKeys[i]];
  }
  return { ...bucket, eventsById, lastSyncAt: Date.now() };
}

export function eventsForWindow(
  bucket: EventsCacheBucket | null,
  windowStartMs: number,
  windowEndMs: number,
): DelegateCalendarEventRow[] {
  if (!bucket) return [];
  const out: DelegateCalendarEventRow[] = [];
  const ids = Object.keys(bucket.eventsById);
  for (let i = 0; i < ids.length; i++) {
    const ev = bucket.eventsById[ids[i]];
    if (eventOverlapsWindow(ev, windowStartMs, windowEndMs)) out.push(ev);
  }
  return out;
}

export function pruneCacheToWindow(
  bucket: EventsCacheBucket,
  retainStartMs: number,
  retainEndMs: number,
): EventsCacheBucket {
  const eventsById: Record<string, DelegateCalendarEventRow> = {};
  const ids = Object.keys(bucket.eventsById);
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const ev = bucket.eventsById[id];
    if (eventOverlapsWindow(ev, retainStartMs, retainEndMs)) {
      eventsById[id] = ev;
    }
  }
  return {
    ...bucket,
    windowStartMs: retainStartMs,
    windowEndMs: retainEndMs,
    eventsById,
  };
}
